import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { ButtonOutline } from '../components/Button';

const orbPulse = keyframes`
  0%, 100% { opacity: 0.10; transform: scale(1) rotate(0deg); }
  50%       { opacity: 0.22; transform: scale(1.08) rotate(3deg); }
`;

const scanLine = keyframes`
  0%   { top: -4px; opacity: 0; }
  5%   { opacity: 0.6; }
  95%  { opacity: 0.6; }
  100% { top: calc(100% + 4px); opacity: 0; }
`;

const Section = styled.section`
  padding: 140px 80px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.mid};
  @media (max-width: 900px) { padding: 80px 24px; }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const TextSide = styled.div``;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 100px);
  line-height: 0.9;
  letter-spacing: 0.02em;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.gradients.brand};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 50px rgba(0,163,255,0.4));
`;

const Subtitle = styled.p`
  font-size: 17px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 14px;
  letter-spacing: 0.04em;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const Desc = styled.p`
  font-size: 15px;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 36px;
`;

const HighlightList = styled.ul`
  list-style: none;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 13px;
`;

const HighlightItem = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.blue};
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border: 1px solid rgba(0,163,255,0.5);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const ButtonStore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 15px 38px;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #C800FF, #00A3FF);
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #00A3FF, #C800FF);
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::before { opacity: 1; }
  & > * { position: relative; z-index: 1; }
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 0 40px rgba(0,163,255,0.4), 0 0 80px rgba(200,0,255,0.2);
  }

  @media (max-width: 480px) { padding: 13px 28px; font-size: 12px; }
`;

const Visual = styled.div`
  position: relative;
  height: 480px;
  background: linear-gradient(135deg, rgba(0,163,255,0.05) 0%, rgba(107,0,255,0.05) 100%);
  border: 1px solid rgba(0,163,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: 900px) { height: 320px; }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0,163,255,0.6), rgba(200,0,255,0.4), transparent);
  animation: ${scanLine} 5s linear infinite;
  pointer-events: none;
`;

const GlowOrbBlue = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0,163,255,0.2) 0%, transparent 70%);
  top: 10%;
  left: 5%;
  animation: ${orbPulse} 5s ease-in-out infinite;
`;

const GlowOrbMagenta = styled.div`
  position: absolute;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200,0,255,0.15) 0%, transparent 70%);
  bottom: 0;
  right: 0;
  animation: ${orbPulse} 6s ease-in-out 1s infinite;
`;

const StoreWordmark = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
`;

const StoreTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 6vw, 80px);
  line-height: 0.88;
  letter-spacing: 0.06em;
  background: linear-gradient(135deg, #C800FF, #00A3FF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 40px rgba(0,163,255,0.5));
`;

const StoreTagline = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.32em;
  color: rgba(255,255,255,0.35);
  margin-top: 18px;
  text-transform: uppercase;
`;

const LiveBadge = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 9px;
  letter-spacing: 0.3em;
  color: #00FF88;
  border: 1px solid rgba(0,255,136,0.35);
  padding: 7px 14px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 7px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #00FF88;
    display: inline-block;
    box-shadow: 0 0 8px #00FF88;
    animation: ${orbPulse} 2s ease-in-out infinite;
  }
`;

const TopAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, #C800FF, #00A3FF, transparent);
`;

export default function StorePreview() {
  const { t } = useTranslation();
  const highlights = t('storePreview.highlights', { returnObjects: true });

  return (
    <Section id="store-preview">
      <Inner>
        <AnimatedSection direction="left">
          <TextSide>
            <SectionLabel>{t('storePreview.sectionLabel')}</SectionLabel>
            <Title>{t('storePreview.title')}</Title>
            <Subtitle>{t('storePreview.subtitle')}</Subtitle>
            <Desc>{t('storePreview.desc')}</Desc>

            <HighlightList>
              {highlights.map((h, i) => (
                <HighlightItem key={i}>{h}</HighlightItem>
              ))}
            </HighlightList>

            <Actions>
              <ButtonStore
                href="https://store.games.guebly.com.br"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>🛒</span>
                <span>{t('storePreview.btnStore')}</span>
              </ButtonStore>
              <ButtonOutline
                href="https://dev.games.guebly.com.br"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('storePreview.btnPublish')}
              </ButtonOutline>
            </Actions>
          </TextSide>
        </AnimatedSection>

        <AnimatedSection direction="right">
          <Visual>
            <TopAccent />
            <ScanLine />
            <GlowOrbBlue />
            <GlowOrbMagenta />
            <StoreWordmark>
              <StoreTitle>GUEBLY</StoreTitle>
              <StoreTitle>STORE</StoreTitle>
              <StoreTagline>store.games.guebly.com.br</StoreTagline>
            </StoreWordmark>
            <LiveBadge>{t('storePreview.comingSoon')}</LiveBadge>
          </Visual>
        </AnimatedSection>
      </Inner>
    </Section>
  );
}
